import CancelSharpIcon from "@mui/icons-material/CancelSharp";
import { Box, Skeleton, Tooltip, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useFacilityWideMessages } from "../../hooks/useFacilityWideMessages";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { STATUS } from "../../theme/tokens";
import { formatPersonFullName } from "../../utils/formatters";
import { SystemFlatSwitch } from "../UIToolkit/Clickable/SystemFlatSwitch";
import ColorfulRole from "../UIToolkit/ColorfulRole/ColorfulRole";
import useStaffCommunication from "./useStaffCommunication";
import { FacilityAnnouncements } from "./util/Announcements/FacilityAnnouncements";
import { FacilityMessageTag } from "./util/Announcements/FacilityMessageTag";
import { AnonymousThreadPreview, AnonymousThreadView } from "./util/AnonymousThread";
import { SendBroadcastMessage } from "./util/Broadcast/SendBroadcastMessage";
import { ChatThreadsFilterSelector } from "./util/ChatThreadsFilterSelector";
import CommunicationThread from "./util/CommunicationThread";
import { StaffCommunicationNurseSearch } from "./util/StaffCommunicationNurseSearch";
import { ThreadMessageTypography, ThreadPreviewButton, ThreadPreviewList, ThreadPreviewListItem } from "./util/styled";
import { failedStatusToString } from "./util/utils";

/** What the panel lists: one-on-one conversations, or the building's announcements. */
enum ChatView {
	Conversations = "conversations",
	Announcements = "announcements",
}

/**
 * StaffCommunication component is used to display and manage communication threads
 *
 */
export const StaffCommunication: FC = () => {
	const {
		unfilteredThreads,
		concatenatedThreads,
		selectedUserId,
		setSelectedUserId,
		clearUserSelection,
		nurseSearchPhrase,
		setNurseSearchPhrase,
		selectedNurse,
		thread,
		handlePostNewMessage,
		userIdNurseMap,
		isRosterError,
		setSelectedFilterStatuses,
		selectedFilterStatuses,
		unreadCounts,
		showUnreadOnly,
		setShowUnreadOnly,
		hasOlderMessages,
		loadOlderMessages,
		isLoadingOlderMessages,
		isThreadLoading,
	} = useStaffCommunication();

	const { t } = useTranslation();
	const { isAnyDemo } = useDepartmentFlags();
	const formatPersonName = useFormatPersonName();
	const [isAnonymousThreadOpen, setIsAnonymousThreadOpen] = useState(false);
	const [view, setView] = useState<ChatView>(ChatView.Conversations);
	// The Announcements list belongs to the department's facility announcements feature.
	const { canView: canViewAnnouncements } = useFacilityWideMessages();
	const showsAnnouncements = canViewAnnouncements && view === ChatView.Announcements;

	if (isAnonymousThreadOpen) {
		return <AnonymousThreadView onClose={() => setIsAnonymousThreadOpen(false)} />;
	}

	if (!selectedUserId || !thread || !selectedNurse) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 24,
					height: "100%",
					width: "100%",
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						width: "100%",
					}}
				>
					<SendBroadcastMessage />
				</Box>
				{canViewAnnouncements && (
					<SystemFlatSwitch<ChatView>
						options={[
							{ label: t("chat.view.conversations"), value: ChatView.Conversations, "data-testid": "chat-view-conversations" },
							{ label: t("chat.view.announcements"), value: ChatView.Announcements, "data-testid": "chat-view-announcements" },
						]}
						activeIndexOrValue={view}
						onSwitch={(option) => setView(option.value)}
						// The list below changes with the choice, so it follows it at once.
						delayCallback={0}
						equalWidthOptions
						textWrap="nowrap"
						rootWidth="100%"
						shape="rectangular"
						data-testid="chat-view-switch"
					/>
				)}
				{showsAnnouncements ? (
					<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", width: "100%" }}>
						<FacilityAnnouncements />
					</Box>
				) : (
					<>
						<StaffCommunicationNurseSearch value={nurseSearchPhrase} setValue={setNurseSearchPhrase} />
						<ChatThreadsFilterSelector
							selectedFilterStatuses={selectedFilterStatuses}
							setSelectedFilterStatuses={setSelectedFilterStatuses}
							threads={unfilteredThreads ?? {}}
							unreadCounts={unreadCounts}
							showUnreadOnly={showUnreadOnly}
							setShowUnreadOnly={setShowUnreadOnly}
						/>
						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								overflowY: "auto",
								width: "100%",
							}}
						>
							<ThreadPreviewList>
								{isAnyDemo && <AnonymousThreadPreview onClick={() => setIsAnonymousThreadOpen(true)} />}
								{isRosterError ? (
									<Typography color="error" data-testid="staff-communication-roster-error">
										{t("profile.roster.unavailable")}
									</Typography>
								) : concatenatedThreads ? (
									concatenatedThreads.map(({ userId, firstMessage, unreadCount }) => {
										const nurse = userIdNurseMap[userId];
										const isFailedStatus = firstMessage?.status ? failedStatusToString(firstMessage.status) : null;
										if (!nurse) return null;

										// Both name orders, so the search matches whichever order the department displays.
										const searchPhrase = nurseSearchPhrase.toLowerCase();
										const matchesSearch = [formatPersonFullName(nurse, false), formatPersonFullName(nurse, true)].some((name) =>
											name.toLowerCase().includes(searchPhrase),
										);
										if (nurseSearchPhrase.trim() && !matchesSearch) {
											return null;
										}

										return (
											<ThreadPreviewListItem key={userId}>
												<ThreadPreviewButton
													isFailedStatus={!!isFailedStatus}
													isEmptyMessage={!firstMessage || !firstMessage.body}
													onClick={() => {
														setSelectedUserId(nurse.userId);
													}}
												>
													<span
														style={{
															justifySelf: "start",
															textTransform: "capitalize",
															fontWeight: 600,
															gridRow: 1,
															gridColumn: 1,
															display: "flex",
															justifyItems: "center",
															alignItems: "center",
															gap: 7,
														}}
													>
														<ColorfulRole role={nurse.role} />
														<Tooltip title={formatPersonName(nurse)}>
															<span>{formatPersonName(nurse)}</span>
														</Tooltip>
													</span>
													<ThreadMessageTypography
														style={{
															opacity: "50%",
															justifySelf: "end",
															gridRow: 1,
															gridColumn: 2,
														}}
														title={firstMessage ? firstMessage.dateCreated.toString() : ""}
													>
														{firstMessage ? firstMessage.dateCreated.toRelative() : ""}
													</ThreadMessageTypography>

													<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, justifySelf: "start", gridRow: 2, gridColumn: 1 }}>
														{firstMessage?.announcementId && <FacilityMessageTag dataTestId={`chat-thread-facility-tag-${userId}`} />}
														<ThreadMessageTypography style={{ maxWidth: 250 }} title={firstMessage ? firstMessage.body : ""}>
															{firstMessage ? firstMessage.body : ""}
														</ThreadMessageTypography>
													</Box>
													{unreadCount !== 0 && (
														<Box
															sx={{
																borderRadius: "50%",
																backgroundColor: STATUS.SUCCESS.main,
																color: "white",
																width: "18px",
																height: "18px",
																justifySelf: "end",
																gridRow: 2,
																gridColumn: 2,
																fontWeight: 600,
																fontSize: 12,
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
															}}
														>
															{unreadCount}
														</Box>
													)}

													{!!isFailedStatus && (
														<Box
															style={{
																display: "flex",
																alignItems: "center",
																justifyContent: "start",
																color: STATUS.ERROR.main,
																fontSize: 12,
																gridRow: 3,
																gridColumn: 1,
															}}
															sx={{
																gap: "4px",
															}}
														>
															<ThreadMessageTypography isError={!!isFailedStatus}>{isFailedStatus}</ThreadMessageTypography>
															<CancelSharpIcon style={{ fontSize: 14 }} />
														</Box>
													)}
												</ThreadPreviewButton>
											</ThreadPreviewListItem>
										);
									})
								) : (
									new Array(10).fill(1).map((_, index) => <Skeleton key={index} variant={"rounded"} width={"100%"} height={"70px"} />)
								)}
							</ThreadPreviewList>
						</Box>
					</>
				)}
			</div>
		);
	}

	return (
		<CommunicationThread
			threads={thread}
			nurse={selectedNurse}
			clearUserSelection={clearUserSelection}
			postNewMessage={handlePostNewMessage}
			hasOlderMessages={hasOlderMessages}
			loadOlderMessages={loadOlderMessages}
			isLoadingOlderMessages={isLoadingOlderMessages}
			isThreadLoading={isThreadLoading}
		/>
	);
};
